import * as tg from "generic-type-guard";
import { SagaIterator } from "redux-saga";
import { call, put, SagaGenerator, select, takeEvery, takeLatest } from "typed-redux-saga";

import { ActivityActions, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { ApplicationActions } from "@com.mgmtp.a12.client/client-core/lib/core/application";
import { WorkflowsActions } from "@com.mgmtp.a12.workflows/workflows-core/lib/actions";

import { isWorkflowActivity } from "../../../utilities/activityUtils";
import { createOpenTaskDocumentPayloadFromProcessInstanceId } from "../../../utilities/processInstanceHelpers";
import { pullWfDocumentWithMetadata, takeReconciledCompletionState } from "../../../utilities/workflowUtils";
import { isDocumentWithProcessMetadata } from "../../workflows/data/processMetadata";
import { handleExtractStammdaten } from "../../stammdaten/sagas/extractStammdatenSaga";
import { setLastCreatedProcessBusinessKey } from "../../../app/slices/operation/reducers";

import { openTaskDocument } from "../store/actions";

export const isModulFWorkflowsActionsVariables = new tg.IsInterface()
	.withProperties({
		documentReference: tg.isString,
		initialActivityId: tg.isString
	})
	.get();

export function* openProcessAfterStartSaga(): SagaIterator<void> {
	yield* takeLatest(
		WorkflowsActions.startProcess.done.match,
		function* handleStartProcessDone(action): SagaGenerator<void> {
			const variables = action.payload.params.variables;
			if (!isModulFWorkflowsActionsVariables(variables)) {
				yield* put(ApplicationActions.setBusy(false));
				return;
			}

			yield* put(
				setLastCreatedProcessBusinessKey({ lastCreatedProcessBusinessKey: variables.documentReference })
			);

			const result = yield* call(pullWfDocumentWithMetadata, action.payload.params.businessKey);
			if (!result?.docSpec) {
				yield* put(ApplicationActions.setBusy(false));
				return;
			}

			const openTaskDocumentPayload = yield* call(
				createOpenTaskDocumentPayloadFromProcessInstanceId,
				result,
				variables.initialActivityId
			);

			if (!openTaskDocumentPayload) {
				yield* put(ApplicationActions.setBusy(false));
				return;
			}

			yield* put(openTaskDocument(openTaskDocumentPayload));
		}
	);
}

export function* openProcessAfterCompleteSaga(): SagaIterator<void> {
	yield* takeEvery(WorkflowsActions.completeTask.done, function* (action) {
		const reconciledState = yield* call(
			takeReconciledCompletionState,
			action.payload.params.activityId,
			action.payload.params.docRef
		);
		const activity = yield* select(ActivitySelectors.activityById(action.payload.params.activityId));
		const initiatingActivityId = activity?.initiatingActivityId;

		if (!initiatingActivityId || !isWorkflowActivity(activity) || !activity.descriptor.instance) {
			return;
		}

		const result =
			reconciledState ??
			(yield* call(
				pullWfDocumentWithMetadata,
				action.payload.params.docRef,
				action.payload.params.document.A12WF.Task.ID
			));
		if (!result?.docSpec || !isDocumentWithProcessMetadata(result.docSpec.document)) {
			yield* put(ActivityActions.cancel({ activityId: action.payload.params.activityId }));
			yield* put(ApplicationActions.setBusy(false));
			return;
		}

		yield* call(
			handleExtractStammdaten,
			result.docSpec.docRef,
			result.docSpec.document.root.processMetadata.fachverfahren.id,
			activity.id
		);

		if (!result.docSpec.document.root.processMetadata.isInUserTask) {
			yield* put(ActivityActions.cancel({ activityId: action.payload.params.activityId }));
			yield* put(ApplicationActions.setBusy(false));
			return;
		}

		const document = {
			...result.docSpec.document,
			id: result.docSpec.docRef,
			modelId: result.docSpec.documentModelName
		};

		yield* put(
			openTaskDocument({
				instance: document.id,
				initiatingActivityId,
				document,
				hasTaskPermission:
					result.hasPermission !== undefined
						? { isLoaded: true, hasPermission: result.hasPermission }
						: { isLoaded: false }
			})
		);
	});
}
